'use server';

import { createClient } from '@/lib/supabase/server';
import { revalidatePath } from 'next/cache';
import { requirePermission } from '@/lib/permissions';
import {
  type EffectivePermissions,
  type SidebarNavConfig,
  type SidebarOrderSettings,
  isSidebarRouteAlwaysEnabled,
  parseSidebarNav,
  resolveEffectiveSidebarNav,
} from '@/lib/permissionTypes';
import { isStaffRole } from '@/lib/roles';
import { mergeEffectivePermissions, type StaffShell } from '@/lib/staffShell';
import { noteServerInvocation } from '@/lib/perf/serverIsolate';

// A 'use server' file may only export async functions — re-exporting types or
// constants here (as this file used to) compiles fine but crashes at runtime
// module-evaluation under Next.js's server-action manifest generation
// ("X is not defined" for whatever was re-exported). The shared types/consts
// (CapabilityKey, EffectivePermissions, CAPABILITY_KEYS, SidebarOrderSettings)
// live in src/lib/permissionTypes.ts, which has zero server-only imports —
// client components must import them from there, never from this file or
// from src/lib/permissions.ts (both transitively import next/headers via the
// Supabase server client, which breaks if pulled into a client bundle).

/**
 * Resolves the calling staff member's own effective permissions — any staff
 * member may read their own (needed client-side to decide what Sidebar/pages
 * to show); editing someone else's is top-role-only (see below).
 */
export async function getMyEffectivePermissions(): Promise<EffectivePermissions> {
  const shell = await resolveMyStaffShell();
  return shell.permissions;
}

/**
 * One round trip for the staff shell: auth, profile, title defaults, overrides,
 * and sidebar nav. Replaces the old pair of getMyEffectivePermissions +
 * getMySidebarNav, which each repeated getUser() and a profile read.
 */
export async function getMyStaffShell(): Promise<StaffShell> {
  return resolveMyStaffShell();
}

async function resolveMyStaffShell(): Promise<StaffShell> {
  const iso = noteServerInvocation('getMyStaffShell');
  const started = performance.now();
  const supabase = await createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: profile } = await supabase
    .from('profiles')
    .select('role, title, is_superuser')
    .eq('id', user.id)
    .single();
  if (!isStaffRole(profile?.role)) throw new Error('Forbidden');

  const title = profile?.title ?? null;
  const isSuperuser = profile?.is_superuser === true;

  const [titleRowRes, overrideRes, settingRes] = await Promise.all([
    title
      ? supabase.from('staff_titles').select('permissions, sidebar_order').eq('title', title).maybeSingle()
      : Promise.resolve({ data: null as null }),
    isSuperuser
      ? Promise.resolve({ data: null as null })
      : supabase.from('staff_permission_overrides').select('permissions').eq('profile_id', user.id).maybeSingle(),
    supabase.from('app_settings').select('value').eq('key', SIDEBAR_ORDER_KEY).maybeSingle(),
  ]);

  const permissions = mergeEffectivePermissions(
    { role: profile?.role ?? null, title, isSuperuser },
    (titleRowRes.data?.permissions ?? null) as Record<string, boolean> | null,
    (overrideRes.data?.permissions ?? null) as Record<string, boolean> | null,
  );
  const nav = resolveEffectiveSidebarNav(
    title ? titleRowRes.data?.sidebar_order : null,
    settingRes.data?.value,
  );

  const serverMs = Math.round(performance.now() - started);
  if (serverMs >= 5000) {
    console.warn('[perf] getMyStaffShell slow', {
      serverMs,
      coldStart: iso.coldStart,
      isolateAgeMs: iso.isolateAgeMs,
      callIndex: iso.callIndex,
    });
  }

  return {
    userId: user.id,
    permissions,
    nav,
    timing: {
      serverMs,
      coldStart: iso.coldStart,
      isolateAgeMs: iso.isolateAgeMs,
      callIndex: iso.callIndex,
    },
  };
}

export interface StaffTitleRow {
  title: string;
  permissions: Record<string, boolean>;
}

export async function getStaffTitles(): Promise<StaffTitleRow[]> {
  await requirePermission('manage_permissions');
  const supabase = await createClient();
  const { data, error } = await supabase
    .from('staff_titles')
    .select('title, permissions')
    .order('title', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as unknown as StaffTitleRow[];
}

/**
 * Creates a new title (with all-false defaults) or updates an existing title's
 * default permission map. Title names are free text by design (see migration
 * 020) so lab_supervisor/production_manager can add new titles without a
 * migration.
 */
export async function upsertStaffTitle(title: string, permissions: Record<string, boolean>): Promise<void> {
  await requirePermission('manage_permissions');
  const trimmed = title.trim();
  if (!trimmed) throw new Error('Title cannot be empty.');
  const supabase = await createClient();
  // Merge with whatever's already stored rather than replacing the whole
  // jsonb blob — capability keys and notification-preference keys share this
  // same column/row, and this function is called by editors for each that
  // only know about their own key set. A blind replace here would silently
  // wipe the other editor's keys whenever one saves after the other.
  const { data: existing } = await supabase
    .from('staff_titles')
    .select('permissions')
    .eq('title', trimmed)
    .maybeSingle();
  const merged = { ...((existing?.permissions ?? {}) as Record<string, boolean>), ...permissions };
  const { error } = await supabase
    .from('staff_titles')
    .upsert({ title: trimmed, permissions: merged, updated_at: new Date().toISOString() });
  if (error) throw new Error(error.message);
  revalidatePath('/staff/admin');
}

/** Sets which title a staff member holds. Pass null to clear it (top-role/student rows don't use title). */
export async function setStaffMemberTitle(profileId: string, title: string | null): Promise<void> {
  await requirePermission('manage_permissions');
  const supabase = await createClient();
  const { error } = await supabase
    .from('profiles')
    .update({ title: title?.trim() || null })
    .eq('id', profileId);
  if (error) throw new Error(error.message);
  revalidatePath('/staff/admin');
}

export async function getPermissionOverride(profileId: string): Promise<Record<string, boolean>> {
  await requirePermission('manage_permissions');
  const supabase = await createClient();
  const { data, error } = await supabase
    .from('staff_permission_overrides')
    .select('permissions')
    .eq('profile_id', profileId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data?.permissions ?? {}) as Record<string, boolean>;
}

/**
 * Sets per-person permission exceptions. Only pass keys that should override
 * the title default — omitted keys keep inheriting from staff_titles. Passing
 * an empty object clears all overrides for this person (reverts to pure
 * title-default behavior) rather than leaving stale keys behind.
 */
export async function setPermissionOverride(profileId: string, permissions: Record<string, boolean>): Promise<void> {
  await requirePermission('manage_permissions');
  const supabase = await createClient();
  const { error } = await supabase
    .from('staff_permission_overrides')
    .upsert({ profile_id: profileId, permissions, updated_at: new Date().toISOString() });
  if (error) throw new Error(error.message);
  revalidatePath('/staff/admin');
}

// ── Sidebar order + page access (migration 025; disabled list added 2026-08) ─
// A route-path ordering layered the same way as permissions: one global
// default, with optional per-title overrides on top (null/absent title
// override = inherit the global default). Config may be a legacy string[]
// (order only) or `{ order, disabled }` for on/off page access.

const SIDEBAR_ORDER_KEY = 'sidebar_order_default';

function serializeSidebarNavForSettings(config: SidebarNavConfig): string {
  return JSON.stringify({
    order: config.order,
    disabled: config.disabled.filter(to => !isSidebarRouteAlwaysEnabled(to)),
  });
}

function normalizeNavConfig(config: SidebarNavConfig): SidebarNavConfig {
  return {
    order: config.order,
    disabled: config.disabled.filter(to => !isSidebarRouteAlwaysEnabled(to)),
  };
}

/**
 * Resolves the calling staff member's effective sidebar nav: their title's
 * override if one is set, else the global default. Prefer getMyStaffShell —
 * this wrapper exists for older callers and runs the same single round trip.
 */
export async function getMySidebarNav(): Promise<SidebarNavConfig> {
  const shell = await resolveMyStaffShell();
  return shell.nav;
}

/** @deprecated Prefer getMySidebarNav — kept for any callers that only need order. */
export async function getMySidebarOrder(): Promise<string[]> {
  const nav = await getMySidebarNav();
  return nav.order;
}

/** Reads the global default plus every title's override (or null) — everything the sidebar-order settings UI needs in one call. Top-role-only. */
export async function getSidebarOrderSettings(): Promise<SidebarOrderSettings> {
  await requirePermission('manage_permissions');
  const supabase = await createClient();

  const [{ data: settingRow }, { data: titleRows }] = await Promise.all([
    supabase.from('app_settings').select('value').eq('key', SIDEBAR_ORDER_KEY).maybeSingle(),
    supabase.from('staff_titles').select('title, sidebar_order').order('title', { ascending: true }),
  ]);

  const globalDefault = parseSidebarNav(settingRow?.value);
  const titleOverrides: Record<string, SidebarNavConfig | null> = {};
  for (const row of (titleRows ?? []) as { title: string; sidebar_order: unknown }[]) {
    const raw = row.sidebar_order;
    if (raw == null || (Array.isArray(raw) && raw.length === 0)) {
      titleOverrides[row.title] = null;
    } else {
      titleOverrides[row.title] = parseSidebarNav(raw);
    }
  }
  return { globalDefault, titleOverrides };
}

/** Sets the global default sidebar order + disabled routes. Top-role-only. */
export async function setGlobalSidebarOrder(config: SidebarNavConfig): Promise<void> {
  await requirePermission('manage_permissions');
  const supabase = await createClient();
  const { error } = await supabase
    .from('app_settings')
    .upsert({
      key: SIDEBAR_ORDER_KEY,
      value: serializeSidebarNavForSettings(normalizeNavConfig(config)),
      updated_at: new Date().toISOString(),
    });
  if (error) throw new Error(error.message);
  revalidatePath('/staff/admin');
}

/** Sets (or, passing null, clears) a specific title's sidebar nav override. Top-role-only. */
export async function setTitleSidebarOrder(title: string, config: SidebarNavConfig | null): Promise<void> {
  await requirePermission('manage_permissions');
  const supabase = await createClient();
  const payload = config ? normalizeNavConfig(config) : null;
  const { error } = await supabase
    .from('staff_titles')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .update({ sidebar_order: payload as any, updated_at: new Date().toISOString() })
    .eq('title', title);
  if (error) throw new Error(error.message);
  revalidatePath('/staff/admin');
}
